import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, LinkIcon, MessageSquare, Tag, Users } from 'lucide-react';
import { IPEntity } from '../types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface IPViewModalProps {
  ip: IPEntity;
  onClose: () => void;
}

interface ImageCache {
  [key: string]: {
    aspectRatio: number;
    loaded: boolean;
  };
}

export function IPViewModal({ ip, onClose }: IPViewModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageCache, setImageCache] = useState<ImageCache>({});

  useEffect(() => {
    ip.images.forEach((image, index) => {
      preloadImage(image.url, index);
    });
  }, []);

  const preloadImage = (url: string, index: number) => {
    if (imageCache[url]?.loaded) return;

    const img = new Image();
    img.onload = () => {
      setImageCache(prev => ({
        ...prev,
        [url]: {
          aspectRatio: img.naturalWidth / img.naturalHeight,
          loaded: true
        }
      }));
    };
    img.src = url;
  };

  const nextImage = () => {
    const nextIndex = currentImageIndex === ip.images.length - 1 ? 0 : currentImageIndex + 1;
    const nextNextIndex = nextIndex === ip.images.length - 1 ? 0 : nextIndex + 1;
    preloadImage(ip.images[nextNextIndex].url, nextNextIndex);
    setCurrentImageIndex(nextIndex);
  };

  const previousImage = () => {
    const prevIndex = currentImageIndex === 0 ? ip.images.length - 1 : currentImageIndex - 1;
    const prevPrevIndex = prevIndex === 0 ? ip.images.length - 1 : prevIndex - 1;
    preloadImage(ip.images[prevPrevIndex].url, prevPrevIndex);
    setCurrentImageIndex(prevIndex);
  };

  const currentImage = ip.images[currentImageIndex];
  const currentAspectRatio = currentImage ? imageCache[currentImage.url]?.aspectRatio : null;
  const isImageLoaded = currentImage ? imageCache[currentImage.url]?.loaded : false;

  const getImageStyle = () => {
    if (!currentAspectRatio) return {};
    
    // For nearly square images (aspect ratio between 0.95 and 1.05)
    if (currentAspectRatio >= 0.95 && currentAspectRatio <= 1.05) {
      return {
        width: '80%',
        height: '80%',
        objectFit: 'contain'
      };
    }
    
    // For landscape images (wider than tall)
    if (currentAspectRatio > 1.05) {
      return {
        width: '100%',
        height: 'auto',
        maxHeight: '90%',
        objectFit: 'contain'
      };
    }
    
    // For portrait images (taller than wide)
    return {
      width: 'auto',
      height: '90%',
      maxWidth: '90%',
      objectFit: 'contain'
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Fixed header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{ip.title}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {/* Images */}
            {ip.images.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
                <div className="relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ height: '500px' }}>
                  {isImageLoaded && (
                    <img
                      src={currentImage.url}
                      alt={`${ip.title} - Image ${currentImageIndex + 1}`}
                      style={getImageStyle()}
                      className="transition-all duration-200"
                    />
                  )}
                  {ip.images.length > 1 && (
                    <>
                      <button
                        onClick={previousImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        {currentImageIndex + 1} / {ip.images.length}
                      </div>
                    </>
                  )}
                </div>
                {ip.images.length > 1 && (
                  <div className="mt-4 grid grid-cols-6 gap-2">
                    {ip.images.map((image, index) => (
                      <button
                        key={image.id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                          currentImageIndex === index
                            ? 'border-indigo-500 opacity-100'
                            : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={image.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Descriptions */}
            {ip.descriptions.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                <div className="space-y-4">
                  {ip.descriptions.map((desc) => (
                    <div key={desc.id} className="prose prose-sm max-w-none text-gray-600">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {desc.content}
                      </ReactMarkdown>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {ip.links.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Links</h3>
                <div className="space-y-2">
                  {ip.links.map((link) => (
                    <div key={link.id} className="flex items-center space-x-2">
                      <LinkIcon className="w-4 h-4 text-gray-400" />
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-500"
                      >
                        {link.title}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {ip.notes.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                <div className="space-y-4">
                  {ip.notes.map((note) => (
                    <div key={note.id} className="flex items-start space-x-2 bg-gray-50 p-4 rounded-lg">
                      <MessageSquare className="w-4 h-4 text-gray-400 mt-1" />
                      <div className="flex-1">
                        <p className="text-gray-600">{note.content}</p>
                        <p className="text-sm text-gray-400 mt-1">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {ip.tags.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {ip.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      <Tag className="w-4 h-4 mr-1" />
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Team */}
            {ip.staff.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Team</h3>
                <div className="flex flex-wrap gap-2">
                  {ip.staff.map((member) => (
                    <span
                      key={member.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                    >
                      <Users className="w-4 h-4 mr-1" />
                      {member.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}